import { filter } from 'lodash';
import { messageFailureStatuses } from 'constants/index';
import actionTypes from '../actions/actionTypes';
import { updateMessage, markMessagesRead, excludeExternalEmailSubject } from '../utils';

const INITIAL_STATE = {
  productLink: '',
  attachments: [],
  recipients: [],
  isLoadingClients: false,
  error: null,
  imageSource: [],
  imageLoading: false,
  imageError: null,
  clients: {
    data: [],
    hasMore: false,
  },
  messages: {
    data: [],
    loading: false,
    error: null,
  },
  sharedProducts: {
    data: {},
    isLoading: false,
    error: null,
  },
  assets: [],
  renderedEmail: {
    isLoading: false,
    html: '',
  },
  sharedEmailLook: null,
  textMessage: '',
  styleModuleIndex: '',
  sharedContents: {
    data: [],
    size: 0,
    count: 0,
    loading: false,
    error: null,
  },
};

export const mapMessagesData = (payload) =>
  payload
    .filter((message) => message.PlatformMessageId)
    .map((message) => ({
      messageId: message.PlatformMessageId,
      messageType: message.MessageType,
      messageStatus: message.MessageStatus?.toLowerCase(),
      dateCreated: message.DateCreated,
      direction: message.Direction,
      body: message.Body,
      subject: message.Subject && excludeExternalEmailSubject(message.Subject),
      contactName: message.ExternalEntityName,
      mediaUrls: message.MediaUrls || message.Attachments,
      templateAttributes: message.TemplateAttributes || {},
      tags: message.Tags ? JSON.parse(message.Tags) : {},
      isFailed: messageFailureStatuses.includes(message.MessageStatus?.toLowerCase()),
      conversationId: message.ConversationId,
      readTimeStamp: message.Read,
      attachmentsAvailable: message.AttachmentsAvailable,
    }));

export const chatConversation = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case actionTypes.GET_ATTACHMENTS_DATASOURCE_SUCCESS:
      return {
        ...state,
        imageSource: [...state.imageSource, ...payload],
        imageLoading: false,
        imageError: null,
      };

    case actionTypes.GET_ATTACHMENTS_DATASOURCE_LOADING:
      return {
        ...state,
        imageLoading: true,
        imageError: null,
        attachments: [...state.attachments, ...payload],
      };

    case actionTypes.GET_ATTACHMENTS_DATASOURCE_ERROR:
      return {
        ...state,
        imageLoading: false,
        imageError: payload,
      };

    case actionTypes.REMOVE_ATTACHMENTS:
      return {
        ...state,
        attachments: payload
          ? filter(state.attachments, (att) => {
              return att.id !== payload;
            })
          : [],
        imageSource: payload
          ? filter(state.imageSource, (att) => {
              return att.id !== payload;
            })
          : [],
        assets: payload
          ? filter(state.assets, (att) => {
              return att.id !== payload;
            })
          : [],
      };

    case actionTypes.ADD_NEW_REPCIPIENT:
      return {
        ...state,
        recipients: [...state.recipients, payload],
      };

    case actionTypes.ADD_NEW_RECIPIENTS:
      return {
        ...state,
        recipients: [...state.recipients, ...payload],
      };

    case actionTypes.REPLACE_RECIPIENTS:
      return {
        ...state,
        recipients: payload,
      };

    case actionTypes.DELETE_REPCIPIENT:
      return {
        ...state,
        recipients: state.recipients.filter((r) => r.contact !== payload.contact),
      };

    case actionTypes.CLIENTS_LIST_LOADING:
      return {
        ...state,
        isLoadingClients: true,
        error: null,
      };

    case actionTypes.CLIENTS_LIST_ERROR:
      return {
        ...state,
        isLoadingClients: false,
        error: payload,
      };

    case actionTypes.CLIENTS_LIST_SUCCESS:
      return {
        ...state,
        isLoadingClients: false,
        clients: {
          data: payload.page > 1 ? [...state.clients.data, ...payload.data] : payload.data,
          hasMore: payload.hasMore,
        },
      };

    case actionTypes.MESSAGES_LIST_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          data: mapMessagesData(payload),
          loading: false,
          error: false,
        },
      };

    case actionTypes.MESSAGES_LIST_V2_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          data: [...state.messages.data, ...mapMessagesData(payload?.items)],
          count: payload?.count,
          loading: false,
          error: false,
        },
      };

    case actionTypes.CLEAR_MESSAGES_LIST:
      return {
        ...state,
        messages: {
          ...state.messages,
          data: [],
          count: 0,
          loading: false,
          error: false,
        },
      };

    case actionTypes.MESSAGES_LIST_LOADING:
    case actionTypes.MESSAGES_LIST_V2_LOADING:
      return {
        ...state,
        messages: {
          ...state.messages,
          loading: true,
        },
      };

    case actionTypes.MESSAGES_LIST_ERROR:
    case actionTypes.MESSAGES_LIST_V2_ERROR:
      return {
        ...state,
        messages: {
          ...state.messages,
          loading: false,
          error: true,
        },
      };

    case actionTypes.ADD_MESSAGE:
    case actionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: {
          ...state.messages,
          data: updateMessage(state.messages.data, payload, state),
        },
      };

    case actionTypes.ADD_TEXT_MESSAGE:
      return {
        ...state,
        textMessage: payload,
      };

    case actionTypes.MARK_MESSAGE_READ:
      return {
        ...state,
        messages: {
          ...state.messages,
          data: markMessagesRead(state.messages.data, payload),
        },
      };

    case actionTypes.RESET_CONVERSATION:
      return {
        ...state,
        attachments: [],
        imageSource: [],
        recipients: [],
        assets: [],
        error: null,
      };

    case actionTypes.SHARED_PRODUCT_DETAILS_SUCCESS:
      return {
        ...state,
        sharedProducts: {
          ...state.sharedProducts,
          data: {
            ...state.sharedProducts.data,
            [payload.id]: payload,
          },
          isLoading: false,
          error: false,
        },
      };

    case actionTypes.SHARED_PRODUCT_DETAILS_LOADING:
      return {
        ...state,
        sharedProducts: {
          ...state.sharedProducts,
          isLoading: true,
        },
      };

    case actionTypes.SHARED_PRODUCT_DETAILS_ERROR:
      return {
        ...state,
        sharedProducts: {
          ...state.sharedProducts,
          data: {
            ...state.sharedProducts.data,
            [payload.id]: payload,
          },
          isLoading: false,
          error: true,
        },
      };

    case actionTypes.STORE_PRODUCT_LINK:
      return {
        ...state,
        productLink: payload,
      };

    case actionTypes.ADD_MARKETING_ASSETS:
      return {
        ...state,
        assets: [...state.assets, ...payload],
        attachments: [...state.attachments, ...payload],
      };

    case actionTypes.EMAIL_TEMPLATE_LOADING:
      return {
        ...state,
        renderedEmail: {
          isLoading: true,
          html: '',
        },
      };

    case actionTypes.EMAIL_TEMPLATE_SUCCESS:
      return {
        ...state,
        renderedEmail: {
          isLoading: false,
          html: payload,
        },
      };

    case actionTypes.EMAIL_TEMPLATE_ERROR:
      return {
        ...state,
        renderedEmail: {
          isLoading: false,
          html: '',
        },
      };

    case actionTypes.CLEAR_EMAIL_TEMPLATE:
      return {
        ...state,
        renderedEmail: {
          isLoading: false,
          html: '',
        },
      };
    case actionTypes.SET_EMAIL_LOOK_DATA:
      return {
        ...state,
        sharedEmailLook: payload,
      };

    case actionTypes.ADD_STYLE_MODULE_HISTORY_INDEX:
      return {
        ...state,
        styleModuleIndex: payload,
      };

    case actionTypes.GET_SHARED_CONTENTS_LOADING:
      return {
        ...state,
        sharedContents: {
          ...state.sharedContents,
          loading: true,
        },
      };

    case actionTypes.GET_SHARED_CONTENTS_SUCCESS:
      return {
        ...state,
        sharedContents: {
          ...state.sharedContents,
          data: [...state.sharedContents.data, ...mapMessagesData(payload?.items)],
          size: state?.sharedContents?.size + payload?.items.length,
          count: payload?.count,
          loading: false,
          error: false,
        },
      };

    case actionTypes.CLEAR_SHARED_CONTENTS:
      return {
        ...state,
        sharedContents: {
          ...state.sharedContents,
          data: [],
          size: 0,
          count: 0,
          loading: false,
          error: false,
        },
      };

    case actionTypes.GET_SHARED_CONTENTS_ERROR:
      return {
        ...state,
        sharedContents: {
          ...state.sharedContents,
          loading: false,
          error: true,
        },
      };

    case actionTypes.TOGGLE_MESSAGES_CHECKBOX:
      return {
        ...state,
        isEditingMessagesEnabled: payload,
      };

    default:
      return state;
  }
};

export default chatConversation;
